import { FC } from 'react';
import { FormikErrors, FormikTouched, FieldArrayRenderProps as FormikFieldArrayRenderProps } from 'formik';
import * as yup from 'yup';
import { ArraySchema, BooleanSchema, DateSchema, MixedSchema, NumberSchema, ObjectSchema, StringSchema } from 'yup';
import {
  CustomFieldDefinitionModel,
  FieldKeyModel,
  LocationTypeModel,
  ValueModel,
  FieldConstraintModelInput
} from '@utmost.co/enterprise-models';

import { ThreeLetterDateFormats } from '@utmost.co/utmost-design-system';
import { InputModel, ModelKey, ModelPart } from './forms/utils/model/types';

export type FieldArrayRenderProps = FormikFieldArrayRenderProps;
export interface UserValidation {
  // error message
  required?: [string];

  email?: [string];
  // min num or string (for date), error message
  min?: [string, string] | [string, string, number | string];
  // max num or string (for date), error message
  max?: [string, string] | [string, string, number | string];
  // regex value, error message
  matches?: [string, string];
  allowedCountryCodes?: string[];
}

export interface UserErrorMessages {
  value?: [string];
  min?: [string];
  max?: [string];
}

export interface ComponentPropBase {
  errors?: string;
  error?: boolean;
  touched?: boolean;
  disabled?: boolean;
  required?: boolean;
  name: string;
  type?:
    | 'button'
    | 'checkbox'
    | 'color'
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'file'
    | 'hidden'
    | 'image'
    | 'month'
    | 'number'
    | 'password'
    | 'radio'
    | 'range'
    | 'reset'
    | 'search'
    | 'submit'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week';
  helpText?: string;
  helpTextBelow?: boolean;
  infoText?: string;
  label?: string;
}

export enum ValidationType {
  CUSTOM_FIELD = 'customField',
  OBJECT = 'object',
  STRING = 'string',
  NUMBER = 'number',
  DATE = 'date',
  ARRAY = 'array',
  BOOLEAN = 'boolean'
}

// Built in things that the customer configuration cannot modify
export interface ElementPayload {
  customFieldDefinitionModel?: CustomFieldPayloadModel;
  nationalIdTypes?: NationalIdType[];
  dropdownValues?: ValueModel[];
}

// These element params are passed down to the component and come from the smartform definition (== smartform template means the same thing)
export interface ElementParams extends FieldDataPresentationModifiers {
  readonly?: boolean;
  locationType?: LocationTypeModel;
  linkLabel?: string;
  noSSNLabel?: string;
  groupLabel?: string;
  iso3?: boolean;
  id?: string;
  sort?: boolean;
  hidden?: boolean;
  isMultilineTextField?: boolean;
  forceUserInput?: boolean;
  fileSizeLimit?: string;
  allowedFileTypes?: string;
  dataSource?: string;
  customQuery?: {
    [key: string]: unknown;
  };
  dateFormat?: ThreeLetterDateFormats;
  style?: string;
  // temp way to pass data down...
  data?: any;
}

export interface ElementBase {
  type: string;
  params?: ElementParams;
  payload?: ElementPayload;
  errorMessages?: UserErrorMessages;
}

export interface FieldDataPresentationModifiers {
  excludedValues?: string[];
  allowedValues?: string[];
  requiredValues?: string[];
  readOnlyValues?: string[];
  currency?: {
    allowed?: string[];
    sortAsEntered?: boolean;
  };
  sort?: boolean;
}

/**
 * Represents the data saved by a user for each field. This data is stored in the database.
 */
export interface SmartformFieldConfig extends FieldDataPresentationModifiers {
  label?: string;
  hint?: string;
  multiline?: boolean;
  initialValue?: FieldValue;
  helpText?: string;
  info?: string;
  validation?: UserValidation;
  fieldName?: OptionalFieldName;
  readonly?: boolean;
}

export interface SmartformElement extends ElementBase {
  fields: SmartformFieldConfig[];
}

// the fields element in is what the customer can configure. There is overlap with meta.
export interface ElementWithMeta<Model = InputModel> extends SmartformElement {
  // Meta is the info that we define at the system level (model, other stuff)
  meta: ElementsMetadata<Model>;
}

export type ElementModifier<Element extends { type: string }> = (elements: Element[]) => Element[];

export type OptionalFieldName =
  | 'summary'
  | 'middleName'
  | 'secondaryLastName'
  | 'confirmSsn'
  | 'jobFamilyGroup'
  | 'jobFamily'
  | 'jobClassifications'
  | 'secondaryTerminationReasonModel'
  | 'missingNationalIdConfirmation'
  | 'jobRequestRateRecommendedRates';

export type SingleFieldValue = string | number | undefined;
export type MultiFieldValue = string[] | number[] | undefined;

export type FieldValue = SingleFieldValue | MultiFieldValue;

export interface ElementQueryResult<Model = InputModel> {
  element: ElementWithMeta<Model> | null;
  meta: ElementMetadata<Model>;
  field: SmartformFieldConfig | null;
  sanitizedFieldName: string;
  currentValue: any;
}

/**
 * Data that is passed to each Smartform React Component
 */
export interface SmartformElementData<Model = InputModel> {
  element: ElementWithMeta<Model>;
  touched: FormikTouched<any>;
  errors: FormikErrors<any>;
  isValidating: boolean;
  submitted?: boolean;
  hideEmptyHints?: boolean;
  dependentValues?: (unknown | undefined)[];
  patchError?: { code: string; message: string };
  getElementByFieldKey: (fieldKey: FieldKeyModel, customFieldId?: string) => ElementQueryResult<Model> | null;
  getConstraintsByFieldKey: (
    fieldKey: FieldKeyModel,
    customFieldId?: string
  ) => FieldConstraintModelInput[] | undefined;
}

/**
 * A helper representation of the data contained in each Smartform element field
 */
export interface SmartformFieldData<Model = any> extends FieldDataPresentationModifiers {
  name: string;
  meta?: ElementMetadata<Model>;
  initialValue: any;
  hint: string;
  helpText: string;
  infoText: string;
  label: string;
  touched: boolean;
  readonly: boolean;
  error?: string;
  validation: {
    required?: boolean;
    email?: boolean;
    min?: [string, string] | [string, string, string | number];
    max?: [string, string] | [string, string, string | number];
    minDate?: string | Date;
    minDateMessage?: string;
    maxDate?: string | Date;
    maxDateMessage?: string;
    matches?: string;
    allowedCountryCodes?: string[];
  };
}

export type ModelTarget<Model = InputModel> = ModelPart<Model> | ModelKey<Model>;
export type InitialValueMapper = (value: string | undefined, element: ElementWithMeta) => any;

export type ElementMetadata<Model = any, URN extends string = string> = {
  urn: URN;
  fieldKey?: FieldKeyModel;
  validation?: FieldValidation;
  initialValueMapper?: InitialValueMapper;
  model: ModelTarget<Model>;
  /** Model path compared instead of `model` in diff mode (and submitted instead of it in deep diff mode; shallow mode
   * still submits the root of the path). Use it when `model` points to a parent object (e.g. a field resolving its own
   * target key at runtime), so deep diff mode does not submit the whole parent. */
  diffModel?: ModelTarget<Model>;
  defaultField: SmartformFieldConfig; // gives us defaults that we combine with anything ElementField definition coming from SmartForms user provided definitions
  fieldName?: OptionalFieldName; // use this property when declaring optional fields
  supports?: (keyof FieldDataPresentationModifiers)[]; // specify which data modifiers are supported by your field
  receivesDataPresentationModifiersFromElementParams?: boolean; // mark which field gets FieldDataPresentationModifiers from element params
  /** For fieldKey/watchFieldKeysForBlur consumers only: when `model` resolves to an array/object of nested
   * fields (e.g. a FieldArray of rows), restricts the blur signal to leaves whose path ends in this key
   * (e.g. 'nationalIdValue'), rather than firing for a change to any nested sub-field. Ignored for
   * scalar-bound fields. */
  blurLeafKey?: string;
  /** For fieldKey/watchFieldKeysForBlur consumers only: opts this field out of the automatic
   * touched-transition blur detection entirely. Use this when the field's own component sets Formik
   * `touched` for reasons other than a real blur (e.g. to surface inline validation while the user is still
   * typing) - such fields must report their own real blur explicitly via `onFieldBlur` from
   * `useSmartFormContext()`, since the automatic detector can't tell a "touched for validation" transition
   * apart from a real one. */
  manualBlurOnly?: boolean;
};

export type ElementsMetadata<Model = InputModel, URN extends string = string> = ElementMetadata<Model, URN>[];

export type AnySchema<T = any> =
  | MixedSchema
  | StringSchema
  | NumberSchema
  | BooleanSchema
  | DateSchema
  | ArraySchema<T>
  | ObjectSchema;

export type BuiltInValidation = (
  schema: AnySchema,
  userValidation: UserValidation,
  errorMessages?: UserErrorMessages
) => AnySchema;

export interface FieldValidation {
  type: ValidationType;
  builtin?: BuiltInValidation;
  subtype?: ValidationType;
  subTypeSchema?: yup.Schema<any>;
}

export interface ValidationObject<Type extends string> extends FieldValidation {
  elementType: Type;
  userValidation?: UserValidation;
  errorMessages?: UserErrorMessages;
  fieldIndex: number;
}

export interface ValidationEntry<Type extends string> {
  fields?: ValidationDefinition<Type>;
  validation?: ValidationObject<Type>;
}

export interface ValidationDefinition<Type extends string> {
  [key: string]: ValidationEntry<Type>;
}

export type CustomFieldPayloadModel = Pick<
  CustomFieldDefinitionModel,
  'name' | 'dataType' | 'status' | 'uid' | 'values' | 'multivalued' | 'code'
>;

export interface NationalIdType {
  code: string;
  masks: string[];
  name: string;
  hasValidUntilDate?: boolean;
}

export type SmartFormElements<Type extends string, Model = InputModel> = Map<
  Type,
  { meta: ElementsMetadata<Model>; component: FC<React.PropsWithChildren<SmartformElementData<Model>>> }
>;

export type Tuple<T, N extends number> = N extends N ? (number extends N ? T[] : LocalTupleOf<T, N, []>) : never;
type LocalTupleOf<T, N extends number, R extends unknown[]> = R['length'] extends N ? R : LocalTupleOf<T, N, [T, ...R]>;
